import React, { useState } from 'react';
import { ArrowLeftIcon, CheckIcon, ClockIcon, DatabaseIcon, GaugeIcon, ShieldCheckIcon } from 'lucide-react';
type Step = {
  id: number;
  title: string;
  description: string;
  completed: boolean;
};
interface SampleData {
  basicData: {
    charge: string;
    timestamp: string;
    notes: string;
  };
  tankLevels: {
    tanks: {
      id: string;
      current: number;
      previous: number;
    }[];
  };
  machineParameters: {
    machines: {
      id: string;
      name: string;
      rotor: number;
      pump: number;
      current: number;
      pressure: number;
      temp: number;
    }[];
  };
  granularData: {
    gusseStatus: boolean;
    rostStatus: boolean;
    recipe1: {
      gran: string;
      h2o: string;
      disp: string;
      granTotal: string;
      um: string;
    };
    recipe2: {
      gran: string;
      h2o: string;
      disp: string;
      granTotal: string;
      um: string;
    };
  };
  probeValues: Record<number, Record<string, string>>;
}
interface ManualSampleCaptureProps {
  onClose: () => void;
  onSave: (probeValues: Record<number, Record<string, string>>) => void;
}
export function ManualSampleCapture({
  onClose,
  onSave
}: ManualSampleCaptureProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [steps] = useState<Step[]>([{
    id: 1,
    title: 'QS Probe',
    description: 'Qualitätskontrolle',
    completed: false
  }, {
    id: 2,
    title: 'Test 1',
    description: 'Granulat & Feststoff',
    completed: false
  }, {
    id: 3,
    title: 'Qualitätskontrolle',
    description: 'Standardmessung',
    completed: false
  }]);
  const [sampleData, setSampleData] = useState<SampleData>({
    basicData: {
      charge: '',
      timestamp: new Date().toISOString().slice(0, 16),
      notes: ''
    },
    tankLevels: {
      tanks: [{
        id: 'AT1',
        current: 0,
        previous: 0
      }, {
        id: 'AT2',
        current: 0,
        previous: 0
      }, {
        id: 'Tank 1',
        current: 0,
        previous: 0
      }, {
        id: 'Tank 2',
        current: 0,
        previous: 0
      }]
    },
    machineParameters: {
      machines: [{
        id: '1',
        name: 'Perlmühle 1',
        rotor: 0,
        pump: 0,
        current: 0,
        pressure: 0,
        temp: 0
      }, {
        id: '2',
        name: 'Perlmühle 2',
        rotor: 0,
        pump: 0,
        current: 0,
        pressure: 0,
        temp: 0
      }]
    },
    granularData: {
      gusseStatus: false,
      rostStatus: false,
      recipe1: {
        gran: '',
        h2o: '',
        disp: '',
        granTotal: '',
        um: ''
      },
      recipe2: {
        gran: '',
        h2o: '',
        disp: '',
        granTotal: '',
        um: ''
      }
    },
    probeValues: {
      1: {},
      2: {},
      3: {},
      4: {},
      5: {}
    }
  });
  const handleProbeValueChange = (step: number, fieldId: string, value: string) => {
    setSampleData(prevData => ({
      ...prevData,
      probeValues: {
        ...prevData.probeValues,
        [step]: {
          ...prevData.probeValues[step],
          [fieldId]: value
        }
      }
    }));
  };
  const renderStepContent = () => {
    let fields: {
      label: string;
      type: 'text' | 'number' | 'boolean';
    }[] = [];
    switch (currentStep) {
      case 1:
        fields = [{
          label: 'Haben die Güsse flecken?',
          type: 'boolean'
        }, {
          label: 'Wurde das Rostpaket in der Hammermühle gewechselt?',
          type: 'boolean'
        }];
        break;
      case 2:
        fields = [{
          label: 'Passt das Granulat?',
          type: 'text'
        }, {
          label: 'Entnehme 1 kg Feststoff',
          type: 'number'
        }];
        break;
      case 3:
        fields = [{
          label: 'pH-Wert',
          type: 'number'
        }, {
          label: 'Viskosität',
          type: 'number'
        }, {
          label: 'Bemerkungen zur Konsistenz',
          type: 'text'
        }];
        break;
    }
    const currentProbeValues = sampleData.probeValues[currentStep] || {};
    return <div className="space-y-6 max-w-4xl mx-auto" data-id="element-646">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-647">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-648">
            {steps[currentStep - 1].title}
          </h3>
          <div className="grid grid-cols-1 gap-6" data-id="element-649">
            {fields.map((field, index) => <div key={index} className="space-y-2" data-id="element-650">
                <label className="block text-sm font-medium text-gray-700" data-id="element-651">
                  {field.label}
                </label>
                {field.type === 'boolean' ? <div className="flex gap-4" data-id="element-652">
                    <label className="inline-flex items-center" data-id="element-653">
                      <input type="radio" value="true" checked={currentProbeValues[`field${index + 1}`] === 'true'} onChange={e => handleProbeValueChange(currentStep, `field${index + 1}`, e.target.value)} className="form-radio text-[#3498dd] focus:ring-[#3498dd]" data-id="element-654" />
                      <span className="ml-2" data-id="element-655">Ja</span>
                    </label>
                    <label className="inline-flex items-center" data-id="element-656">
                      <input type="radio" value="false" checked={currentProbeValues[`field${index + 1}`] === 'false'} onChange={e => handleProbeValueChange(currentStep, `field${index + 1}`, e.target.value)} className="form-radio text-[#3498dd] focus:ring-[#3498dd]" data-id="element-657" />
                      <span className="ml-2" data-id="element-658">Nein</span>
                    </label>
                  </div> : <input type={field.type === 'number' ? 'number' : 'text'} step={field.type === 'number' ? '0.1' : undefined} value={currentProbeValues[`field${index + 1}`] || ''} onChange={e => handleProbeValueChange(currentStep, `field${index + 1}`, e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#3498dd]" placeholder={`${field.type === 'number' ? 'Wert' : 'Text'} eingeben`} data-id="element-659" />}
              </div>)}
            <div data-id="element-660">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-661">
                Bemerkungen
              </label>
              <textarea value={currentProbeValues['notes'] || ''} onChange={e => handleProbeValueChange(currentStep, 'notes', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#3498dd]" rows={3} placeholder="Optionale Bemerkungen zur Probe" data-id="element-662" />
            </div>
          </div>
        </div>
      </div>;
  };
  const handleSubmit = () => {
    // Hier würde die Logik zum Speichern der Probe implementiert
    console.log('Probe gespeichert:', sampleData);
    // Send the probe values back to the parent component
    onSave(sampleData.probeValues);
    // Close the modal
    onClose();
  };
  return <div className="fixed inset-0 bg-white z-50 overflow-auto" data-id="element-663">
      <div className="max-w-6xl mx-auto px-4 py-6" data-id="element-664">
        <div className="flex items-center justify-between mb-8" data-id="element-665">
          <button onClick={onClose} className="flex items-center text-gray-600 hover:text-gray-800" data-id="element-666">
            <ArrowLeftIcon size={20} className="mr-2" data-id="element-667" />
            Zurück zur Übersicht
          </button>
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-668">
            Neue Probe erfassen
          </h2>
          <div className="w-24" data-id="element-669" />
        </div>
        <div className="mb-8" data-id="element-670">
          <div className="flex items-center justify-between relative" data-id="element-671">
            <div className="absolute left-0 top-[15px] h-1 bg-gray-200 w-full -z-10" data-id="element-672" />
            <div className="absolute left-0 top-[15px] h-1 bg-[#3498dd] transition-all duration-300 -z-10" style={{
            width: `${(currentStep - 1) / (steps.length - 1) * 100}%`
          }} data-id="element-673" />
            {steps.map(step => <div key={step.id} className={`flex flex-col items-center ${step.id < currentStep ? 'text-[#3498dd]' : step.id === currentStep ? 'text-[#3498dd]' : 'text-gray-400'}`} data-id="element-674">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 border-2 ${step.id < currentStep ? 'bg-[#3498dd] border-[#3498dd] text-white' : step.id === currentStep ? 'border-[#3498dd] bg-white' : 'border-gray-300 bg-white'}`} data-id="element-675">
                  {step.id < currentStep ? <CheckIcon size={16} data-id="element-676" /> : step.id}
                </div>
                <div className="text-sm font-medium" data-id="element-677">{step.title}</div>
                <div className="text-xs text-gray-500" data-id="element-678">{step.description}</div>
              </div>)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-6 mb-6" data-id="element-679">
          {renderStepContent()}
        </div>
        <div className="flex justify-between" data-id="element-680">
          <button onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} className={`px-4 py-2 text-sm rounded ${currentStep === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} disabled={currentStep === 1} data-id="element-681">
            Zurück
          </button>
          <button onClick={() => {
          if (currentStep < steps.length) {
            setCurrentStep(currentStep + 1);
          } else {
            handleSubmit();
          }
        }} className="px-4 py-2 text-sm text-white bg-[#3498dd] rounded hover:bg-blue-600" data-id="element-682">
            {currentStep === steps.length ? 'Probe speichern' : 'Weiter'}
          </button>
        </div>
      </div>
    </div>;
}